// Utility for user preference helpers
import { USER_WORKER_URL, isSfwSite } from '../config.js';
import { storage } from './storage.js';
import { isOnboardingDisabled } from './featureFlags.js';
import { tokenManager } from './TokenManager.js';

export function setTempGenderPreference(isMale){
  storage.set('temp_gender_preference', isMale? 'male':'female');
}

export function getGenderPreference(){
  try {
    const tempPreference = storage.get('temp_gender_preference');
    if(tempPreference){return tempPreference==='male';}
    const userData=storage.get('userData');
    if(userData){
      if(userData && userData.gender_preference!==undefined){
        if(typeof userData.gender_preference==='boolean')return userData.gender_preference;
        return userData.gender_preference==='male_only';
      }
    }
    const genderPreference=storage.get('gender_preference');
    if(genderPreference!==null){return genderPreference==='true';}
    return false;
  }catch(e){console.error('[userPrefs] error',e);return false;}
}

export function setGenderPreference(isMale){
  try {
    // Persist to main key so the preference survives reloads
    storage.set('gender_preference', isMale ? 'true' : 'false');

    // Also update cached userData object if it exists so the value travels with the user when they later log-in
    const userData = storage.get('userData');
    if (userData) {
      // store as boolean for backward compatibility; components convert to enum when needed
      storage.updateJson('userData', 'gender_preference', isMale);
    }

    // Update temporary preference so current in-memory components see the change immediately
    setTempGenderPreference(isMale);
  } catch (e) {
    console.error('[userPrefs] setGenderPreference error', e);
  }
}

export function normalizeStyle(value){
  return value === 'realism' ? 'realism' : 'anime';
}

export const getCharacterFormStyle = () =>
  normalizeStyle(document.getElementById('ai-character-style')?.value);

export function getStylePreference(){
  try {
    const saved = storage.get('character_style_preference');
    if(saved) return normalizeStyle(saved);
    const userData = storage.get('userData');
    if(userData && userData.character_style_preference !== undefined){
      return normalizeStyle(userData.character_style_preference);
    }
    return 'anime';
  }catch(e){console.error('[userPrefs] getStylePreference error',e);return 'anime';}
}

export function setStylePreference(style){
  try {
    const normalized = normalizeStyle(style);
    storage.set('character_style_preference', normalized);
    const userData = storage.get('userData');
    if (userData) {
      storage.updateJson('userData', 'character_style_preference', normalized);
    }
  } catch (e) {
    console.error('[userPrefs] setStylePreference error', e);
  }
}

const parseStoredBoolean = (value) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
};

export function hasAgePreference() {
  try {
    const userData = storage.get('userData') || {};
    if (typeof userData.over_18 === 'boolean') return true;
    return parseStoredBoolean(storage.get('over_18')) !== null;
  } catch (e) {
    console.error('[userPrefs] hasAgePreference error', e);
    return false;
  }
}

export function getOver18() {
  try {
    const userData = storage.get('userData') || {};
    if (typeof userData.over_18 === 'boolean') return userData.over_18 === true;
    const stored = parseStoredBoolean(storage.get('over_18'));
    if (stored !== null) return stored;
    return false;
  } catch (e) {
    console.error('[userPrefs] getOver18 error', e);
    return false;
  }
}

export function getNsfwEnabled() {
  if (isSfwSite()) return false;
  if (!getOver18()) return false;
  try {
    const userData = storage.get('userData') || {};
    if (typeof userData.nsfw === 'boolean') return userData.nsfw === true;
    const stored = parseStoredBoolean(storage.get('nsfw'));
    if (stored !== null) return stored;
    return false;
  } catch (e) {
    console.error('[userPrefs] getNsfwEnabled error', e);
    return false;
  }
}

export function getCardAnimationEnabled() {
  try {
    const stored = parseStoredBoolean(storage.get('rankings_card_animated'));
    if (stored !== null) return stored;
    return true;
  } catch (e) {
    console.error('[userPrefs] getCardAnimationEnabled error', e);
    return true;
  }
}

export function setCardAnimationEnabled(animated) {
  try {
    storage.set('rankings_card_animated', animated ? 'true' : 'false');
    document.dispatchEvent(new CustomEvent('cardAnimationPreferenceSelected', {
      detail: { animated: animated === true }
    }));
  } catch (e) {
    console.error('[userPrefs] setCardAnimationEnabled error', e);
  }
}

const applyAgeContentPrefsLocally = ({ over18, nsfw, skipScroll = false } = {}) => {
  const nextOver18 = over18 === true;
  const nextNsfw = !isSfwSite() && nextOver18 && nsfw === true;
  storage.set('over_18', nextOver18 ? 'true' : 'false');
  storage.set('nsfw', nextNsfw ? 'true' : 'false');
  const userData = storage.get('userData');
  if (userData) {
    storage.updateJson('userData', 'over_18', nextOver18);
    storage.updateJson('userData', 'nsfw', nextNsfw);
  }
  document.dispatchEvent(new CustomEvent('nsfwPreferenceSelected', {
    detail: { over18: nextOver18, nsfw: nextNsfw, skipScroll: skipScroll === true }
  }));
  return { over18: nextOver18, nsfw: nextNsfw };
};

export async function setAgeContentPrefs({ over18, nsfw, persist = true, skipScroll = false } = {}) {
  try {
    const previousOver18 = getOver18();
    const previousNsfw = getNsfwEnabled();
    const next = applyAgeContentPrefsLocally({ over18, nsfw, skipScroll });
    if (persist === false) return { ok: true, attempted: false };
    const result = await persistAgeContentPrefsToServer(next);
    if (result.ok === false && result.attempted) {
      applyAgeContentPrefsLocally({ over18: previousOver18, nsfw: previousNsfw, skipScroll });
    }
    return result;
  } catch (e) {
    console.error('[userPrefs] setAgeContentPrefs error', e);
    return { ok: false, attempted: false };
  }
}

export const persistAgeContentPrefsToServer = async ({ over18, nsfw } = {}) => {
  const nextOver18 = over18 === true;
  const nextNsfw = !isSfwSite() && nextOver18 && nsfw === true;
  try {
    const userData = storage.get('userData') || {};
    const isAuthenticated = userData.isLoggedIn === true || tokenManager.isAuthenticated();
    if (!isAuthenticated) return { ok: true, attempted: false };

    const token = await tokenManager.getValidAccessToken();
    if (!token) {
      console.error('[userPrefs] persistAgeContentPrefsToServer error', 'missing_access_token');
      return { ok: false, attempted: true };
    }

    const response = await fetch(`${USER_WORKER_URL}/update-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ over_18: nextOver18, nsfw: nextNsfw })
    });
    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }
    const data = await response.json().catch(() => ({}));
    if (data?.success === false) {
      throw new Error(data.error || 'Failed to persist age content prefs');
    }
    return { ok: true, attempted: true };
  } catch (error) {
    console.error('[userPrefs] persistAgeContentPrefsToServer error', error.message || error);
    return { ok: false, attempted: true };
  }
};

export function resolveLoginAgePrefs(dbUser) {
  const dbOver18 = dbUser?.over_18 === true;
  const dbNsfw = dbUser?.nsfw === true;
  const guestOver18 = hasAgePreference() ? getOver18() : false;
  const guestNsfw = guestOver18 && getNsfwEnabled();

  if (guestNsfw && !dbNsfw) {
    const over18 = dbOver18 || guestOver18;
    if (over18) {
      return { over18: true, nsfw: true, shouldSyncGuest: true };
    }
  }

  if (dbOver18 || dbNsfw) {
    return { over18: dbOver18, nsfw: dbOver18 && dbNsfw, shouldSyncGuest: false };
  }
  if (!hasAgePreference()) {
    return { over18: false, nsfw: false, shouldSyncGuest: false };
  }
  return { over18: guestOver18, nsfw: guestNsfw, shouldSyncGuest: true };
}

export function shouldShowGenderPreferenceModal() {
  if (isOnboardingDisabled()) return false;

  try {
    const userData = storage.get('userData') || {};
    if (userData?.isLoggedIn) return false;
  } catch (e) {
    console.error('[userPrefs] shouldShowGenderPreferenceModal userData error', e);
  }

  try {
    const genderPreference = storage.get('gender_preference');
    if (genderPreference !== null && hasAgePreference()) return false;
  } catch (e) {
    console.error('[userPrefs] shouldShowGenderPreferenceModal gender_preference error', e);
  }

  return true;
}